import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useNavigate } from "react-router-dom";
import OrderDetailRow from "../../components/form/OrderDetailRow";
import {
	Alert,
	Button,
	Divider,
	Input,
	Modal,
	Radio,
	RadioChangeEvent,
} from "antd";

import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import { ColDef } from "ag-grid-enterprise";
import api from "../../utils/axios";
import {
	codeFormatter,
	getEndOfTheMonth,
	getStartOfTheMonth,
	priceFormatter,
	valueDateFormatter,
} from "../../utils/formatters";
import classes from "./DriverDetails.module.css";
import { ReactComponent as NotepadIcon } from "../../assets/NotepadIcon.svg";
import { ReactComponent as EditIcon } from "../../assets/EditIcon.svg";
import { ReactComponent as Calculator } from "../../assets/Calculator.svg";
import { ReactComponent as Printer } from "../../assets/Printer.svg";
import CustomChartLineCard from "../../components/CustomLineChartCard";
import { toast } from "react-toastify";
import { MinusCircleOutlined } from "@ant-design/icons";
import Buttons from "../../components/form/Buttons";
import { fetchUserCashRegister } from "../../lib/OptionListApi";
import { useConfigurationNumber } from "../../hooks/useConfiguration";
import { ConfigurationId } from "../../types/Lookups";
import { fetchZonesCached } from "../../lib/cachedLookups";
import DefaultSelect from "../../components/form/DefaultSelect";
import {
	Checkbox,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Select,
	useMediaQuery,
} from "@mui/material";
import GridCheckbox from "../../components/GridCheckbox";
import useLang from "../../hooks/useLang";
import {
	exportDataToCsv,
	moneyExportColumns,
} from "../../utils/exportDataToCsv";
import { ReactComponent as ExportIcon } from "../../assets/arrow-download-small.svg";
import DefaultDatePicker from "../../components/form/DefaultDatePicker";
import DefaultInput from "../../components/form/DefaultInput";
import { CONFIG } from "../../config";
import useCurrencies from "../../hooks/useCurrencies";
import {
	currencySymbol,
	priceFormatterByCurrency,
} from "../../utils/formatters";
import { CURRENCY_EUR, CURRENCY_LEK } from "../../api/currency";
import CurrencyBadge from "../../components/CurrencyBadge";

interface MonthlyDriverData {
	id: string;
	driverPrice: number;
	orderPrice: number;
	date: string;
	description: string;
	/** The currency the order was registered in — what `orderPrice` is denominated in. */
	currencyId?: number;
	/** The currency the driver actually collected. Only differs on a EUR order paid in LEK. */
	deliveredCurrencyId?: number;
}

/**
 * Sum `orderPrice` per currency — never across them
 * (the one rule in [docs/dual-currency/README.md](../../../docs/dual-currency/README.md)).
 *
 * Rows are bucketed on `currencyId`, the currency the order was **registered** in, because that
 * is the currency `orderPrice` is denominated in. The driver is ultimately settled on what they
 * actually collected (`deliveredCurrencyId`), but `Sales/dailydriversales` returns no converted
 * amount for a EUR order handed in as LEK, so bucketing these raw prices on the collected
 * currency would print a LEK symbol on a EUR figure. The owed/settled totals come from the API,
 * which does convert.
 *
 * With `dualCurrencyEnabled` false every row lands in `local`, so Kosovo and Macedonia — where
 * orders are registered in EUR and DEN respectively — keep exactly the single figure they had.
 */
const sumByRegistrationCurrency = (
	rows: MonthlyDriverData[],
	dualCurrencyEnabled: boolean
) =>
	(rows ?? []).reduce(
		(totals: { local: number; eur: number }, row: MonthlyDriverData) => {
			const amount = Number(row?.orderPrice) || 0;
			if (dualCurrencyEnabled && row?.currencyId === CURRENCY_EUR) {
				totals.eur += amount;
			} else {
				totals.local += amount;
			}
			return totals;
		},
		{ local: 0, eur: 0 }
	);

/**
 * Same calendar day, not just the same day-of-month: `DailyDriverSales_sp` returns this month
 * **and** last month, so a day-of-month match alone counts the same date one month back as
 * today's takings.
 */
const isToday = (value: any) => {
	if (!value) {
		return false;
	}
	const date = new Date(value);
	const now = new Date();
	return (
		date.getDate() === now.getDate() &&
		date.getMonth() === now.getMonth() &&
		date.getFullYear() === now.getFullYear()
	);
};
const { TextArea } = Input;
const DriverDetails = () => {
	const urlParams = new URLSearchParams(window.location.search);
	const driverId = urlParams.get("id");
	let defaultExcelExportSettings = {};
	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapHeaderText: true,
			resizable: true,
			filterParams: { excelMode: "windows" },
			autoSizeAllColumns: true,
		}),
		[]
	);

	const formattedFirstDay = getStartOfTheMonth();
	const formattedLastDay = getEndOfTheMonth();
	const navigate = useNavigate();
	let settlementDriverOrderGridRef = useRef(null);
	const [tableColumns, setTableColumns] = useState<ColDef[]>();
	const [tableColumns1, setTableColumns1] = useState<ColDef[]>();
	const [
		settlementDriverOrderTableColumns,
		setSettlementDriverOrderTableColumns,
	] = useState<ColDef[]>();
	const [open, setOpen] = useState<boolean>(false);
	const [openFeeModal, setOpenFeeModal] = useState<boolean>(false);
	const [openSettlementModal, setOpenSettlementModal] =
		useState<boolean>(false);
	const [data, setData] = useState([]);
	const [filteredDailySales, setFilteredDailySales] = useState([]);
	const [monthlyDriverData, setMonthlyDriverData] = useState<any>([]);
	const [filteredMonthlyData, setFilteredMonthlyData] = useState<any>([]);
	const [value, setValue] = useState(1);
	const [userData, setUserData] = useState<any>([]);
	const [name, setName] = useState<string | undefined>();
	const [email, setEmail] = useState<string | undefined>();
	const [username, setUsername] = useState<string | undefined>();
	const [phone, setPhone] = useState<string | undefined>();
	const [identificationNumber, setIdentificationNumber] = useState<
		string | undefined
	>();
	const [address, setAddress] = useState<string | undefined>();
	const [country, setCountry] = useState<string | undefined>();
	const [city, setCity] = useState<string | undefined>();
	const [status, setStatus] = useState<any>();
	const [cashIn, setCashIn] = useState<number>();
	const [unreturnedDriverSales, setUnreturnedDriverSales] = useState<any>();
	const [salesOwedToCompany, setSalesOwedToCompany] = useState<number>(0);
	const [salesReturnedByDriver, setSalesReturnedByDriver] = useState(0);
	// The driver is settled in what they ACTUALLY collected, so the two currencies are
	// tracked and settled separately (Procesi 7).
	const [salesOwedToCompanyEur, setSalesOwedToCompanyEur] =
		useState<number>(0);
	const [salesReturnedByDriverEur, setSalesReturnedByDriverEur] = useState(0);
	const [cashInEur, setCashInEur] = useState<any>(0);
	const { dualCurrencyEnabled, defaultCurrencyId } = useCurrencies();
	const [currentBalance, setCurrentBalance] = useState<any>();
	const [totalIncome, setTotalIncome] = useState<any>();
	const [totalPayment, setTotalPayment] = useState<any>();
	const [lastIncome, setLastIncome] = useState<any>();
	const [lastIncomeDate, setLastIncomeDate] = useState<any>();
	const [lastIncomeComment, setLastIncomeComment] = useState<any>();
	const [amount, setAmount] = useState<any>();
	const [comment, setComment] = useState<any>();
	const [editable, setEditable] = useState<boolean>(false);
	const [openBlockingModal, setOpenBlockingModal] = useState<boolean>(false);
	const [driverZones, setDriverZones] = useState<any>([]);
	const [password, setPassword] = useState<string>("");
	const [confirmPassword, setConfirmPassword] = useState<string>("");
	const [driverName, setDriverName] = useState<string>("");
	const [driverPhone, setDriverPhone] = useState<string>("");
	// The rows the daily grid is showing, rather than their sum: the split depends on
	// `dualCurrencyEnabled`, which resolves after first paint — sometimes later than the grid's
	// own first-render callback fires. Holding the rows lets the totals recompute when it lands.
	const [dailyGridRows, setDailyGridRows] = useState<MonthlyDriverData[]>([]);
	const [monthlyDriverTotal, setMonthlyDriverTotal] = useState<number>(0);
	const [
		openDriverSettlementCashRegisterModal,
		setOpenDriverSettlementCashRegisterModal,
	] = useState<boolean>(false);
	const [settlementDriverOrderData, setSettlementDriverOrderData] = useState<
		any[]
	>([]);
	const [
		driverCashRegisterTransactionId,
		setDriverCashRegisterTransactionId,
	] = useState<number>(0);
	const [dailySalesFromDate, setDailySalesFromDate] = useState<Date | null>(
		null
	);
	const [dailySalesToDate, setDailySalesToDate] = useState<Date | null>(null);
	const [monthlyFromDate, setMonthlyFromDate] = useState<Date | null>(null);
	const [monthlyToDate, setMonthlyToDate] = useState<Date | null>(null);
	const [cashRegisterOptions, setCashRegisterOptions] = useState<any>([]);
	const [selectedCashRegisterId, setSelectedCashRegisterId] = useState<
		any | null
	>(null);
	const personalIdLength = useConfigurationNumber(
		ConfigurationId.PersonalIdLength
	);
	const { strings } = useLang("driverDetails");

	const onChange = (e: RadioChangeEvent) => {
		setValue(e.target.value);
	};

	const getUserCashRegisters = async () => {
		const cashRegisters = await fetchUserCashRegister();
		if (cashRegisters) {
			if (cashRegisters?.length > 1) {
				const contructedCashRegisters = cashRegisters.map(
					(item: any) => ({
						value: item.id,
						label: item.description,
					})
				);
				setCashRegisterOptions(contructedCashRegisters);
			} else if (cashRegisters.length === 1) {
				const cashRegisterId = cashRegisters[0].id;
				setSelectedCashRegisterId(cashRegisterId);
			}
		}
	};
	const fetchDailySalesForDriver = useCallback(async () => {
		try {
			const response = await api.get("Sales/dailydriversales", {
				params: { driverId: driverId },
			});
			const result = response.data;
			setFilteredDailySales(result);
			setData(result);
		} catch (error) {
			console.error("Error fetching orders:", error);
		}
	}, [driverId]);

	const fetchMonthlyDriverBalance = useCallback(async () => {
		try {
			const response = await api.get("Balance/monthlyDriverBalance", {
				params: { driverId: driverId },
			});
			const result = response.data;
			setMonthlyDriverData(result);
			setFilteredMonthlyData(result);
		} catch (error) {
			console.error("Error fetching orders:", error);
		}
	}, [driverId]);

	const getMonthlyDriverPayments = useCallback(async () => {
		try {
			const response = await api.get("Balance/driver-balance", {
				params: { userId: driverId },
			});
			const result = response.data[0];
			setCurrentBalance(result?.currentBalance);
			setTotalIncome(result?.totalIncome);
			setTotalPayment(result?.totalPayment);
			setLastIncome(result?.lastIncome);
			setLastIncomeDate(result?.lastIncomeDate);
			setLastIncomeComment(result?.lastIncomeComment);
		} catch (error) {
			console.error("Error fetching orders:", error);
		}
	}, [driverId]);

	// Today's takings, kept as two independent balances. They are never added together, and
	// each is printed with the symbol of the currency it is actually held in.
	const todaysSales = useMemo(
		() =>
			sumByRegistrationCurrency(
				filteredDailySales.filter((row: MonthlyDriverData) =>
					isToday(row?.date)
				),
				dualCurrencyEnabled
			),
		[filteredDailySales, dualCurrencyEnabled]
	);
	const dailySales = todaysSales.local.toFixed(2);
	const dailySalesEur = todaysSales.eur.toFixed(2);

	// Same treatment for the total under the grid's own filters.
	const dailyGridTotals = useMemo(
		() => sumByRegistrationCurrency(dailyGridRows, dualCurrencyEnabled),
		[dailyGridRows, dualCurrencyEnabled]
	);

	const fetchDriverData = useCallback(async () => {
		try {
			const response = await api.get("User/userbyid", {
				params: {
					id: driverId,
				},
			});
			if (response.status === 200) {
				const result = response.data;
				setInput(result);
				setUserData(result);
				return result;
			}
		} catch (error) {
			console.error("Error fetching data:", error);
		}
	}, [driverId]);

	const fetchDetailedUnreturnedSales = useCallback(async () => {
		try {
			const response = await api.get(
				"Sales/detailed-unreturned-driver-sales",
				{
					params: {
						driverId: driverId,
					},
				}
			);
			const result = response.data;
			setUserData(result);
		} catch (error) {
			console.error("Error fetching data:", error);
		}
	}, [driverId]);

	const fetchUnreturnedDriverSales = async () => {
		try {
			const response = await api.get("Sales/driversalesandsettlements", {
				params: {
					driverId: driverId,
				},
			});
			if (response) {
				const results = response.data[0];
				setSalesOwedToCompany(results.driverSalesOwedToCompany);
				setSalesReturnedByDriver(results.salesReturnedByDriver);
				setSalesOwedToCompanyEur(
					results.driverSalesOwedToCompanyEur ?? 0
				);
				setSalesReturnedByDriverEur(
					results.salesReturnedByDriverEur ?? 0
				);
				setUnreturnedDriverSales(response?.data);
			}
		} catch (error) {
			console.error("Error fetching data:", error);
		}
	};

	const isSmallScreen = useMediaQuery("(max-width: 994px)");

	// Data loads once per driver. It used to share the column effect below, so the currency flag
	// and a language switch reloaded every balance and list on the page (up to three times on AL).
	useEffect(() => {
		fetchBalances();
		fetchDriverData();
		getUserSections();
		getZones();
		getUserCashRegisters();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [driverId]);

	useEffect(() => {
		setTableColumns([
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 50,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "displayId",
				headerName: strings?.orderNumber,
				valueFormatter: (params) => codeFormatter(params.value),
				cellStyle: (params: any) => ({
					fontSize: "15px",
					fontWeight: "400",
					lineHeight: "18px",
					color: "#4F16C2",
					textDecoration: "underline",
				}),
				width: 160,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "driverPrice",
				valueFormatter: (params) => priceFormatter(params.value),
				headerName: strings?.driverPrice,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "date",
				headerName: strings?.date,
				valueFormatter: (params: any) =>
					valueDateFormatter(params?.value),
				suppressSizeToFit: isSmallScreen,
			},
		]);
		setTableColumns1([
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 50,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "displayId",
				headerName: strings?.orderNumber,
				valueFormatter: (params) => codeFormatter(params.value),
				cellStyle: (params: any) => ({
					fontSize: "15px",
					fontWeight: "400",
					lineHeight: "18px",
					color: "#4F16C2",
					textDecoration: "underline",
				}),
				width: 130,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "orderPrice",
				valueFormatter: priceFormatterByCurrency("currencyId"),
				headerName: strings?.orderPrice,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "currencyId",
				headerName:
					strings?.registeredCurrency ?? "Monedha e regjistrimit",
				valueGetter: (params: any) =>
					currencySymbol(params.data?.currencyId),
				width: 150,
				hide: !dualCurrencyEnabled,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "deliveredCurrencyId",
				// Editable only while the row is unsettled; after that the money has already
				// moved into a cash register in a specific currency.
				onCellClicked: (params: any) => {
					if (!dualCurrencyEnabled || params.data?.settled) return;
					if (params.data?.currencyId !== CURRENCY_EUR) return;
					const next =
						params.data?.deliveredCurrencyId === CURRENCY_EUR
							? CURRENCY_LEK
							: CURRENCY_EUR;
					correctCollectedCurrency(params.data.id, next);
				},
				headerName: strings?.collectedCurrency ?? "Monedha e arketuar",
				valueGetter: (params: any) =>
					currencySymbol(params.data?.deliveredCurrencyId),
				width: 150,
				hide: !dualCurrencyEnabled,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "date",
				headerName: strings?.date,
				valueFormatter: (params: any) =>
					valueDateFormatter(params?.value),
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "settled",
				headerName: strings?.settled,
				cellRenderer: GridCheckbox,
				suppressSizeToFit: isSmallScreen,
			},
		]);
		setSettlementDriverOrderTableColumns([
			{
				field: "orderId",
				headerName: strings?.orderNumber,
				valueFormatter: (params) => codeFormatter(params.value),
				cellStyle: (params: any) => ({
					fontSize: "15px",
					fontWeight: "400",
					lineHeight: "18px",
					color: "#4F16C2",
					textDecoration: "underline",
				}),
				width: 130,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "orderPrice",
				valueFormatter: (params) => priceFormatter(params.value),
				headerName: strings?.orderPrice,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "driverPrice",
				headerName: strings?.driverPrice,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "transportPrice",
				headerName: strings?.transportPrice,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "createdAt",
				headerName: strings?.createdAt,
				suppressSizeToFit: isSmallScreen,
			},
		]);
	}, [strings, dualCurrencyEnabled]);

	const fetchBalances = async () => {
		await fetchDailySalesForDriver();
		await fetchMonthlyDriverBalance();
		await getMonthlyDriverPayments();
		await fetchUnreturnedDriverSales();
		await fetchDetailedUnreturnedSales();
	};
	const setInput = (data: any) => {
		setName(data.nameAndSurname);
		setEmail(data.email);
		setUsername(data.username);
		setPhone(data.phone);
		setCountry(data.country);
		setCity(data.city);
		setAddress(data.address);
		setStatus(data.status);
		setDriverName(data.nameAndSurname);
		setDriverPhone(data.phone);
		setIdentificationNumber(data.identificationNumber);
	};

	const clearState = () => {
		setComment("");
		setAmount(0);
	};

	// Procesi 12: the collected currency can be corrected right up until the driver is settled.
	// The API re-checks that under a lock, so this only has to keep the UI honest.
	const correctCollectedCurrency = async (
		orderId: number,
		deliveredCurrencyId: number
	) => {
		try {
			await api.patch("Order/delivered-currency", {
				orderId,
				deliveredCurrencyId,
			});
			toast.success("Monedha e arkëtimit u ndryshua");
			await fetchBalances();
		} catch (error) {
			// The axios interceptor already surfaces the server's message.
			console.log("Error", error);
		}
	};
	const balanceAccount = async () => {
		// A driver may now be carrying only EUR, so a zero local total is not on its own a
		// reason to refuse the settlement.
		//
		// This used to also test `dailySales === 0`, which never matched: `dailySales` is a
		// `toFixed` string and was being compared to a number. What is owed is the thing that
		// decides whether there is anything to settle, so the dead clause is simply gone.
		if (
			salesOwedToCompany === 0 &&
			!(dualCurrencyEnabled && salesOwedToCompanyEur !== 0)
		) {
			toast.warning("Nuk keni shitje te pabarazuara");
			return;
		} else {
			if (!selectedCashRegisterId || selectedCashRegisterId === 0) {
				toast.error("Nuk keni te zgjedhur nje Cash Register");
				return;
			} else {
				const data = {
					driverId: driverId,
					cashIn: salesOwedToCompany,
					cashOut: 0,
					enteredCashInValue: cashIn,
					// Omitted where the deployment is single-currency, so the procedure
					// writes exactly one cash transaction as it always has.
					enteredCashInValueEur: dualCurrencyEnabled
						? cashInEur
						: undefined,
					cashRegisterId: selectedCashRegisterId,
				};
				try {
					const response = await api.put("Settlement", data);
					if (response.status === 200 && response?.data) {
						setCashIn(0);
						setCashInEur(0);
						await fetchBalances();
						setOpenSettlementModal(false);
						toast.success("Sukses");
					}
				} catch (error) {
					console.log("Error", error);
				}
			}
		}
	};

	const paymentForDriver = async () => {
		const data = {
			driverId: driverId,
			cashIn: value === 1 ? parseFloat(amount) : 0.0,
			cashOut: value === 2 ? parseFloat(amount) : 0.0,
			comment: comment,
			switchId: value,
		};
		try {
			const response = await api.post(
				"CashRegisterTransaction/insert-driver-new-transaction",
				data
			);
			if (response.status === 200) {
				clearState();
				await fetchBalances();
				setOpen(false);
				toast.success("Success!");
				return;
			}
		} catch (error) {
			console.log("Error:" + error);
			toast.error("Error:" + error);
		}
	};

	// pagination for first table

	const [currentPageTable1, setCurrentPageTable1] = useState(1);
	const [pageSizeTable1, setPageSizeTable1] = useState(5);

	const totalItems = monthlyDriverData?.length;

	const monthlyDriverDataPaginated = useMemo(() => {
		const startIndex = (currentPageTable1 - 1) * pageSizeTable1;
		const endIndex = startIndex + pageSizeTable1;
		return monthlyDriverData?.slice(startIndex, endIndex);
	}, [currentPageTable1, monthlyDriverData, pageSizeTable1]);

	const startIndex = (currentPageTable1 - 1) * pageSizeTable1 + 1;
	const endIndex =
		startIndex + Math.min(pageSizeTable1, totalItems - startIndex);

	const handlePageChange = (page: any) => {
		setCurrentPageTable1(page);
	};
	const handlePageSizeChange = (current: number, size: number) => {
		setPageSizeTable1(size);
		setCurrentPageTable1(1);
	};

	// pagination for second table

	const [currentPageTable2, setCurrentPageTable2] = useState(1);
	const [pageSizeTable2, setPageSizeTable2] = useState(5);

	const totalDailySalesData = data?.length;

	const dailyDriverDataPaginated = useMemo(() => {
		const startIndexSecond = (currentPageTable2 - 1) * pageSizeTable2;
		const endIndexSecond = startIndexSecond + pageSizeTable2;
		return data?.slice(startIndexSecond, endIndexSecond);
	}, [currentPageTable2, data, pageSizeTable2]);

	const startIndexSecond = (currentPageTable2 - 1) * pageSizeTable2 + 1;
	const endIndexSecond =
		startIndexSecond +
		Math.min(pageSizeTable2, totalDailySalesData - startIndexSecond);

	const handlePageChangeDailyData = (page: any) => {
		setCurrentPageTable2(page);
	};
	const handlePageSizeChangeDailyData = (current: number, size: number) => {
		setPageSizeTable2(size);
		setCurrentPageTable2(1);
	};

	const [sectionDropDown, setSectionDropDown] = useState<
		{ value: number; label: string }[]
	>([]);

	const getZones = useCallback(async () => {
		const allZones = await fetchZonesCached();
		if (allZones) {
			const filteredSections = allZones
				.filter((item: any) => item.levelId === 3)
				.map((item: any) => ({
					value: item.id,
					label: item.description,
				}));
			setSectionDropDown(filteredSections);
		}
	}, []);

	const [newZones, setNewZones] = useState<number[]>([]);
	const [deletedSections, setDeletedSections] = useState<number[]>([]);

	const printDriverBalance = async () => {
		try {
			const response = await api.get("Balance/print-driver-balance", {
				params: { driverId },
				responseType: "blob",
			});
			const file = response.data;
			const fileUrl = window.URL.createObjectURL(file);

			const pdfWindow = window.open();
			if (pdfWindow) {
				pdfWindow.location.href = fileUrl;
			} else {
				toast.error(
					"Popup eshte i bllokuar. Ju lutemi lejoni popup-ët për këtë faqe."
				);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const printDriverDailySales = async () => {
		try {
			const response = await api.get("Balance/print-driver-daily", {
				params: { driverId },
				responseType: "blob",
			});
			const file = response.data;
			const fileUrl = window.URL.createObjectURL(file);

			const pdfWindow = window.open();
			if (pdfWindow) {
				pdfWindow.location.href = fileUrl;
			} else {
				toast.error(
					"Popup eshte i bllokuar. Ju lutemi lejoni popup-ët për këtë faqe."
				);
			}
		} catch (error) {
			console.log(error);
		}
	};
	const saveClientChanges = async () => {
		if (!name) {
			return;
		}

		if (password !== confirmPassword) {
			toast.error("Fjalëkalimet nuk janë të njejtë!");
			return;
		}

		if (password !== "" && !validatePassword(password)) {
			toast.error(
				"Fjalëkalimi duhet të jetë së paku 8 karaktere dhe të përfshijë një shkronjë të madhe dhe një numër."
			);
			return;
		}

		const [firstName, lastName] = name.split(" ");

		if (!firstName) {
			toast.error("Emri është i detyrueshëm");
			return;
		} else if (!lastName) {
			toast.error("Mbiemri është i detyrueshëm");
			return;
		} else if (!phone) {
			toast.error("Numri i telefonit është i detyrueshëm");
			return;
		} else if (!username) {
			toast.error("Username është i detyrueshëm");
			return;
		} else if (!selectedZones?.length) {
			toast.error("Zona është e detyrueshme");
			return;
		} else if (!identificationNumber) {
			toast.error("Nr i leternjoftimit është i detyrueshëm.");
			return false;
		}
		if (identificationNumber.length !== personalIdLength) {
			toast.error(
				`Nr i leternjoftimi duhet te kete ${personalIdLength} karaktere`
			);
			return false;
		}

		const newZonesDto = newZones.map((zoneId: number) => ({
			sectionId: zoneId,
			userId: driverId,
		}));
		const deletedSectionsDto = deletedSections.map((sectionId: number) => ({
			sectionId: sectionId,
			userId: driverId,
		}));
		const data = {
			userId: driverId,
			firstName: firstName,
			lastName: lastName,
			email: email ? email : null,
			phone: phone,
			username: username,
			address: address,
			sections: newZonesDto,
			deletedSections: deletedSectionsDto,
			password: password,
			identificationNumber: identificationNumber,
		};

		const hasDetailsChanged =
			firstName !== userData.firstName ||
			lastName !== userData.lastName ||
			email !== userData.email ||
			phone !== userData.phone ||
			username !== userData.username ||
			address !== userData.address ||
			identificationNumber !== userData.identificationNumber ||
			password;

		const hasZonesChanged =
			newZonesDto?.length > 0 || deletedSectionsDto?.length > 0;
		try {
			const response = await api.post("User/update-driver-details", data);
			if (response.status === 200) {
				setEditable(false);
				// toast.success("Të dhënat u përditësuan me sukses!");
				fetchDriverData();
				getUserSections();
				setPassword("");
				setConfirmPassword("");
				if (hasDetailsChanged && hasZonesChanged) {
					toast.success(
						"Zonat dhe të dhënat u përditësuan me sukses!"
					);
				} else if (hasZonesChanged) {
					toast.success("Zonat u përditësuan me sukses!");
				} else if (hasDetailsChanged) {
					toast.success("Të dhënat u përditësuan me sukses!");
				}
			}
		} catch (error) {
			console.log("Error: ", error);
		}
	};

	const confirmBlocking = async () => {
		try {
			const response = await api.post("User/block-account", null, {
				params: { userId: driverId },
			});
			if (response.status === 200) {
				setOpenBlockingModal(false);
				fetchDriverData();
				navigate("/drivers");
				toast.success("Llogari u bllokua me sukses");
			}
		} catch (error) {
			console.log(error);
		}
	};
	const [selectedZones, setSelectedZones] = useState<any>();
	const getUserSections = async () => {
		try {
			const response = await api.get("Zone/user-sections", {
				params: { userId: driverId },
			});
			if (response.status === 200) {
				const driverZones = response.data;
				setDriverZones(driverZones);
				const zoneIds = driverZones.map((zone: any) => zone.sectionId);
				setSelectedZones(zoneIds);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const isAllSelected =
		sectionDropDown?.length > 0 &&
		selectedZones?.length === sectionDropDown?.length;

	const handleZoneChange = (value: any) => {
		const initialZoneIds = driverZones?.map((zone: any) => zone.sectionId);
		let addedZones = [];
		let removedZones = [];

		if (value[value?.length - 1] === "all") {
			const selected = sectionDropDown.map((zone: any) => zone.value);
			if (selectedZones?.length === selected?.length) {
				setSelectedZones([]);
				addedZones = [];
				removedZones = initialZoneIds?.filter((id: any) =>
					selected.includes(id)
				);
			} else {
				setSelectedZones(selected);
				addedZones = selected.filter(
					(id: any) => !initialZoneIds?.includes(id)
				);
				removedZones = [];
			}
		} else {
			setSelectedZones(value);
			addedZones = value.filter(
				(id: any) => !initialZoneIds?.includes(id)
			);
			removedZones = initialZoneIds?.filter(
				(id: any) => !value.includes(id)
			);
		}
		setNewZones(addedZones);
		setDeletedSections(removedZones);
	};

	const validatePassword = (password: any) => {
		const hasUpperCase = /[A-Z]/.test(password);
		const hasNumber = /[0-9]/.test(password);
		const isValidLength = password.length >= 8;
		return hasUpperCase && hasNumber && isValidLength;
	};

	const handleUsernameChange = (newUsername: string) => {
		if (newUsername.includes(" ")) {
			toast.error("Username nuk duhet te ketë hapsirë");
		} else {
			setUsername(newUsername);
		}
	};
	const onFirstDataRendered = useCallback((params: any) => {
		(dailyDriverGrid.current as any).api.sizeColumnsToFit();
		calculateDailyDriverAggregate();
		calculateMonthlyDriverAggregate();
	}, []);
	const getMonthlyFilterData = () => {
		if (!monthlyDriverGrid?.current) {
			return [];
		}
		let orderData: any = [];
		monthlyDriverGrid?.current?.api.forEachNodeAfterFilter(
			(element: any) => {
				orderData.push(element.data);
			}
		);
		return orderData;
	};
	const getDailyFilterData = () => {
		if (!dailyDriverGrid?.current) {
			return [];
		}
		let orderData: any = [];
		dailyDriverGrid?.current?.api.forEachNodeAfterFilter((element: any) => {
			orderData.push(element.data);
		});
		return orderData;
	};
	const dailyDriverGrid = useRef<any>();
	const monthlyDriverGrid = useRef<any>();
	const calculateMonthlyDriverAggregate = () => {
		let orderData = getMonthlyFilterData();
		let totalVlera = 0;
		if (orderData?.length > 0) {
			for (let i = 0; i < orderData?.length; i++) {
				totalVlera += orderData[i].driverPrice;
			}
		}
		setMonthlyDriverTotal(totalVlera);
	};

	const calculateDailyDriverAggregate = () => {
		setDailyGridRows(getDailyFilterData());
	};

	const monlthyDriverColumn = ["displayId", "driverPrice", "date"];

	/**
	 * The daily sheet is one row per delivered order, so `orderPrice` is denominated by the
	 * currency that order was **registered** in — the same `currencyId` the grid buckets its
	 * totals on. It used to export as a bare number, which made a 6,000 LEK order and a 60 EUR
	 * one indistinguishable on a sheet that is added up by hand.
	 */
	const dailyDriverColumn = [
		"displayId",
		...moneyExportColumns({
			colId: "orderPrice",
			currencyHeaderName: strings?.registeredCurrency,
			currency: (row: any) => row?.currencyId ?? defaultCurrencyId,
			dualCurrencyEnabled,
		}),
		"date",
		"settled",
	];

	const onExportSelectedMonthyDriver = () => {
		exportDataToCsv(monthlyDriverGrid, monlthyDriverColumn);
	};
	const onExportSelecteddailyDriver = () => {
		exportDataToCsv(dailyDriverGrid, dailyDriverColumn);
	};

	const openCashRegisterTransaction = async (rowData: any) => {
		const data = rowData?.data;
		setDriverCashRegisterTransactionId(data?.id);
		if (data.cashTransaction) {
			try {
				const response = await api.get(
					"Settlement/select-driver-settlement-orders",
					{ params: { cashRegisterTransactionId: data.id } }
				);
				if (response.status === 200 && response.data) {
					setSettlementDriverOrderData(response?.data);
					setOpenDriverSettlementCashRegisterModal(true);
				}
			} catch (error) {
				console.log(error);
			}
		} else {
			return;
		}
	};

	const onMonthlyDriverDataGridClicked = async (params: any) => {
		const colId = params.column.colId;
		switch (colId) {
			case "id":
				await onOrderIdClicked(params?.data);
				break;
		}
	};

	const onOrderIdClicked = (data: any) => {
		const orderId = data?.id?.toString();
		// navigate("/order-details", { state: orderId });
		const url = `/order-details?id=${orderId}`;
		window.open(url, "_blank");
	};

	const handleFromDateChange = (date: Date | null) => {
		setMonthlyFromDate(date);
		filterDataByDateRange(date, monthlyToDate);
	};
	const handleToDateChange = (date: Date | null) => {
		setMonthlyToDate(date);
		filterDataByDateRange(monthlyFromDate, date);
	};

	const filterDataByDateRange = (
		fromDate: Date | null,
		toDate: Date | null
	) => {
		if (!fromDate && !toDate) {
			setFilteredMonthlyData(monthlyDriverData);
			return;
		}

		const filtered = monthlyDriverData.filter((data: any) => {
			const itemDate = new Date(data.date);

			if (fromDate) {
				const fromDateObj = new Date(fromDate);
				fromDateObj.setHours(0, 0, 0, 0);
				if (itemDate < fromDateObj) {
					return false;
				}
			}

			if (toDate) {
				const toDateObj = new Date(toDate);
				toDateObj.setHours(23, 59, 59, 999);
				if (itemDate > toDateObj) {
					return false;
				}
			}

			return true;
		});

		setFilteredMonthlyData(filtered);
	};

	const handleDailySalesFromDateChange = (date: Date | null) => {
		setDailySalesFromDate(date);
		filterDailySalesDataByDateRange(date, dailySalesToDate);
	};
	const handleDailySalesToDateChange = (date: Date | null) => {
		setDailySalesToDate(date);
		filterDailySalesDataByDateRange(dailySalesFromDate, date);
	};

	const filterDailySalesDataByDateRange = (
		fromDate: Date | null,
		toDate: Date | null
	) => {
		if (!fromDate && !toDate) {
			setFilteredDailySales(data);
			return;
		}

		const filtered = data.filter((data: any) => {
			const dataDate = new Date(data.date);

			if (fromDate) {
				const fromDateObj = new Date(fromDate);
				fromDateObj.setHours(0, 0, 0, 0);
				if (dataDate < fromDateObj) {
					return false;
				}
			}

			if (toDate) {
				const toDateObj = new Date(toDate);
				toDateObj.setHours(23, 59, 59, 999);
				if (dataDate > toDateObj) {
					return false;
				}
			}

			return true;
		});

		setFilteredDailySales(filtered);
	};

	/**
	 * One currency's half of the SALES card: what the driver still owes, and what they took
	 * today, both in that currency alone.
	 *
	 * LEK and EUR render through this same function so neither can end up styled as a footnote
	 * of the other — they are two accounts the driver settles separately, not one balance and
	 * its remainder ([W-D16](../../../docs/dual-currency/02-DECISIONS.md)).
	 */
	const salesCurrencyRow = (
		currencyId: number,
		owed: number,
		daily: string
	) => (
		<div className={classes.salesCurrencyRow}>
			<div className={classes.salesCurrencyHeadline}>
				<CurrencyBadge currencyId={currencyId} />
				<span>
					{Number(owed ?? 0).toFixed(2)} {currencySymbol(currencyId)}
				</span>
			</div>
			<span className={classes.salesCurrencyDaily}>
				{daily} {currencySymbol(currencyId)} {strings?.dailySales}
			</span>
		</div>
	);

	const onCellClicked = async (params: any) => {
		const colId = params.column.colId;
		switch (colId) {
			case "displayId":
				await onOrderIdClicked(params?.data);
				break;
		}
	};

	return (
		<>
			<div className={classes.driverMainContainer}>
				<div className={classes.secondDriverContainer}>
					<div className={classes.firstContainer}>
						<div className={classes.driverInfos}>
							<p className={classes.driverName}>{driverName}</p>{" "}
							<p className={classes.driverNumber}>
								{driverPhone}
							</p>
							<p className={classes.occupation}>
								{strings?.occupation}
							</p>
						</div>
						<div className={classes.buttonContainer}>
							<Button
								type="default"
								size="large"
								onClick={() => setOpenFeeModal(true)}
								icon={<NotepadIcon />}
								style={{
									display: "flex",
									alignItems: "center",
									color: "#4f16c2",
								}}
							>
								{strings?.deliveryFees}
							</Button>
						</div>
					</div>
					<div className={classes.tablesInfo}>
						<div className={classes.firstInfoDrivers}>
							<p className="driverName">{strings?.driver}</p>
							<div className={classes.buttonsEditBlock}>
								<Button
									type="default"
									size="large"
									icon={<EditIcon />}
									style={{
										display: "flex",
										alignItems: "center",
										color: "#4f16c2",
									}}
									onClick={() => setEditable(true)}
								>
									{strings?.editData}
								</Button>
								<Button
									type="default"
									size="large"
									icon={<MinusCircleOutlined />}
									onClick={() => setOpenBlockingModal(true)}
									style={{
										display: "flex",
										alignItems: "center",
										color: "#4f16c2",
									}}
								>
									{strings?.blockAccount}
								</Button>
							</div>
						</div>
						<div className={classes.infosContainer}>
							<div className={classes.firstInfos}>
								<OrderDetailRow
									label={strings?.nameLabel}
									value={name}
									style={style.oddRow}
									className="driverDetailRow"
									editable={editable}
									onChangeText={setName}
								/>
								<OrderDetailRow
									label={strings?.emailLabel}
									value={email}
									className="driverDetailRow"
									editable={editable}
									onChangeText={setEmail}
								/>
								<OrderDetailRow
									label={strings?.usernameLabel}
									value={username}
									style={style.oddRow}
									className="driverDetailRow"
									editable={editable}
									onChangeText={(newUsername) =>
										handleUsernameChange(newUsername)
									}
								/>
								<OrderDetailRow
									label={strings?.phoneLabel}
									value={phone}
									className="driverDetailRow"
									editable={editable}
									onChangeText={setPhone}
								/>
								<OrderDetailRow
									label={strings?.identificationNumber}
									value={identificationNumber}
									className="driverDetailRow"
									editable={editable}
									onChangeText={setIdentificationNumber}
								/>
								{editable && (
									<OrderDetailRow
										label={strings?.passwordLabel}
										value={password}
										style={style.oddRow}
										className="driverDetailRow"
										editable={editable}
										onChangeText={setPassword}
										type="password"
									/>
								)}
							</div>
							<div className={classes.secondInfos}>
								<div style={style.oddRow}>
									{!editable ? (
										<div className={classes.zonesDiv}>
											<p className="text-base font-bold text-gray-700 font-inter ml-1">
												{strings?.zonesLabel}
											</p>
											<DefaultSelect
												placeholder={driverZones
													.map(
														(zone: any) =>
															zone.description
													)
													.join(", ")}
												className={classes.zonesSelect}
												options={driverZones.map(
													(zone: any) => ({
														key: zone.id,
														value: zone.description,
													})
												)}
											/>
										</div>
									) : (
										<div className={classes.zonesDiv}>
											<p className="text-base font-bold text-gray-700 font-inter ml-1">
												{strings?.zonesLabel}
											</p>
											<Select
												multiple
												value={selectedZones}
												renderValue={(selected) => {
													const selectedLabels: any =
														selected
															.map(
																(
																	selectedZone: any
																) => {
																	const matchingSections =
																		sectionDropDown.filter(
																			(
																				section: any
																			) =>
																				selectedZone ===
																				section.value
																		);
																	return matchingSections.map(
																		(
																			matchingSection: any
																		) =>
																			matchingSection.label
																	);
																}
															)
															.flat()
															.filter(
																(label: any) =>
																	label !== ""
															);

													return selectedLabels.join(
														", "
													);
												}}
												className={
													classes.zonesEditSelect
												}
												onChange={(
													selectedZoneIds: any
												) => {
													handleZoneChange(
														selectedZoneIds.target
															.value
													);
												}}
												MenuProps={{
													PaperProps: {
														style: {
															maxHeight: 230,
															width: "5%",
														},
													},
												}}
											>
												<MenuItem value="all">
													<ListItemIcon>
														<Checkbox
															checked={
																isAllSelected
															}
															indeterminate={
																sectionDropDown?.length >
																	0 &&
																selectedZones?.length <
																	sectionDropDown?.length
															}
														/>
													</ListItemIcon>
													<ListItemText primary="Select All" />
												</MenuItem>
												{sectionDropDown.map(
													(zone: any) => (
														<MenuItem
															key={zone.value}
															value={zone.value}
														>
															<Checkbox
																checked={selectedZones.includes(
																	zone.value
																)}
															/>
															<ListItemText
																primary={
																	zone.label
																}
															/>
														</MenuItem>
													)
												)}
											</Select>
										</div>
									)}
								</div>

								<OrderDetailRow
									label={strings?.cityLabel}
									value={city}
									className="driverDetailRow"
								/>
								<OrderDetailRow
									label={strings?.countryLabel}
									style={style.oddRow}
									value={country}
									className="driverDetailRow"
								/>
								<OrderDetailRow
									label={strings?.activeAccountLabel}
									value={
										status === 1
											? strings?.activeAccountYes
											: strings?.activeAccountNo
									}
									className="driverDetailRow"
								/>

								{editable && (
									<OrderDetailRow
										label={strings?.confirmPasswordLabel}
										value={confirmPassword}
										className="driverDetailRow"
										editable={editable}
										style={style.oddRow}
										onChangeText={setConfirmPassword}
										type="password"
									/>
								)}
							</div>
						</div>
					</div>
					<div className={classes.bilancSalesContainer}>
						<div className={classes.chartlineContainer}>
							<CustomChartLineCard
								title={strings?.currentBalanceLabel}
								firstLine={`${currentBalance} ${CONFIG?.CURRENCY}`}
								secondLine={`${totalPayment} ${CONFIG?.CURRENCY} ${strings?.totalPaid}`}
								thirdLine={`${totalIncome} ${CONFIG?.CURRENCY}${strings?.totalIncome}`}
								style={{ border: "none", boxShadow: "none" }}
							/>
							<div className={classes.buttons}>
								<Button
									type="default"
									size="large"
									icon={<Printer />}
									style={{
										display: "flex",
										alignItems: "center",
										color: "#4f16c2",
									}}
									onClick={printDriverBalance}
								>
									{strings?.printBalance}
								</Button>
								<Button
									type="default"
									icon={<Calculator />}
									size="large"
									style={{
										display: "flex",
										alignItems: "center",
										backgroundColor: "#4f16c2",
										color: "#ffff",
									}}
									onClick={() => setOpen(true)}
								>
									{strings?.payAccount}
								</Button>
							</div>
							<div className="flex justify-center">
								<div
									style={{
										width: "80%",
										height: "1px",
										background: "#F2F2F2",
									}}
								/>
							</div>
							<div className="p-6">
								<p>
									{strings?.totalLabel}{" "}
									{monthlyDriverTotal.toFixed(2)}
								</p>
								<div className="flex justify-between items-center m-2">
									<div className="flex flex-row">
										<DefaultDatePicker
											label={strings?.fromdate}
											value={monthlyFromDate}
											onChange={handleFromDateChange}
											width={172}
										/>
										<DefaultDatePicker
											label={strings?.until}
											value={monthlyToDate}
											onChange={handleToDateChange}
											width={172}
										/>
									</div>
									<Button
										className={classes.buttonExcel}
										onClick={onExportSelectedMonthyDriver}
									>
										<ExportIcon />
										{strings?.exportExcel}
									</Button>
								</div>
								<div
									className="ag-theme-alpine"
									style={{
										height: 350,
										width: "100%",
									}}
								>
									<AgGridReact
										// rowData={monthlyDriverDataPaginated}
										rowData={filteredMonthlyData}
										columnDefs={tableColumns}
										defaultColDef={defaultColDef}
										undoRedoCellEditing={true}
										undoRedoCellEditingLimit={20}
										suppressCopyRowsToClipboard={true}
										defaultExcelExportParams={
											defaultExcelExportSettings
										}
										rowSelection="multiple"
										ref={monthlyDriverGrid}
										enableRangeSelection={true}
										cacheQuickFilter={true}
										onFilterChanged={(event) => {
											if (
												event.type === "filterChanged"
											) {
												calculateMonthlyDriverAggregate();
											}
										}}
										onRowDoubleClicked={
											openCashRegisterTransaction
										}
										onCellClicked={
											onMonthlyDriverDataGridClicked
										}
									/>
								</div>
							</div>
						</div>
						<div className={classes.chartlineContainer}>
							{/* One card, two currencies of equal weight: LEK above the card's
							    own rule, EUR below it, each with its own badge, its own owed
							    figure and its own daily total. Nothing here is ever summed
							    across the two. */}
							<CustomChartLineCard
								title={strings?.salesLabel}
								firstLine={
									dualCurrencyEnabled
										? salesCurrencyRow(
												CURRENCY_LEK,
												salesOwedToCompany,
												dailySales
										  )
										: `${salesOwedToCompany} ${CONFIG?.CURRENCY}`
								}
								secondLine={
									dualCurrencyEnabled
										? salesCurrencyRow(
												CURRENCY_EUR,
												salesOwedToCompanyEur,
												dailySalesEur
										  )
										: `${dailySales} ${CONFIG?.CURRENCY}  ${strings?.dailySales}`
								}
								thirdLine={`${data?.length} ${strings?.deliveredOrders}`}
								style={{ border: "none", boxShadow: "none" }}
							/>
							<div className={classes.buttons}>
								<Button
									type="default"
									size="large"
									icon={<Printer />}
									onClick={printDriverDailySales}
									style={{
										display: "flex",
										alignItems: "center",
										color: "#4f16c2",
									}}
								>
									{strings?.printList}
								</Button>
								<Button
									type="default"
									size="large"
									icon={<Calculator />}
									style={{
										display: "flex",
										alignItems: "center",
										backgroundColor: "#4f16c2",
										color: "#ffff",
									}}
									onClick={() => setOpenSettlementModal(true)}
								>
									{strings?.settleAccount}
								</Button>
							</div>
							<div className="flex justify-center">
								<div
									style={{
										width: "80%",
										height: "1px",
										background: "#F2F2F2",
									}}
								/>
							</div>

							<div className="p-6">
								<p>
									{strings?.totalLabel}{" "}
									{dualCurrencyEnabled
										? `${dailyGridTotals.local.toFixed(
												2
										  )} ${currencySymbol(
												CURRENCY_LEK
										  )}  •  ${dailyGridTotals.eur.toFixed(
												2
										  )} ${currencySymbol(CURRENCY_EUR)}`
										: dailyGridTotals.local.toFixed(2)}
								</p>
								<div className="flex justify-between items-end m-2">
									<div className="flex flex-row">
										<DefaultDatePicker
											label={strings?.fromdate}
											value={dailySalesFromDate}
											onChange={
												handleDailySalesFromDateChange
											}
											width={172}
										/>
										<DefaultDatePicker
											label={strings?.until}
											value={dailySalesToDate}
											onChange={
												handleDailySalesToDateChange
											}
											width={172}
										/>
									</div>
									<Button
										className={classes.buttonExcel}
										onClick={onExportSelecteddailyDriver}
									>
										<ExportIcon />
										{strings?.exportExcel}
									</Button>
								</div>
								<div
									className="ag-theme-alpine"
									style={{
										height: 350,
										width: "100%",
									}}
								>
									<AgGridReact
										rowData={filteredDailySales}
										columnDefs={tableColumns1}
										defaultColDef={defaultColDef}
										undoRedoCellEditing={true}
										undoRedoCellEditingLimit={20}
										suppressCopyRowsToClipboard={true}
										defaultExcelExportParams={
											defaultExcelExportSettings
										}
										rowSelection="multiple"
										ref={dailyDriverGrid}
										enableRangeSelection={true}
										cacheQuickFilter={true}
										onFirstDataRendered={
											onFirstDataRendered
										}
										onCellClicked={onCellClicked}
										onFilterChanged={(event) => {
											if (
												event.type === "filterChanged"
											) {
												calculateDailyDriverAggregate();
											}
										}}
									/>
								</div>
							</div>
						</div>
					</div>
					<Divider />
					<div className="flex justify-end p-6">
						<Buttons
							button1Text={strings?.cancel}
							button2Text={strings?.save}
							onButton2Click={saveClientChanges}
							onButton1Click={() => setEditable(false)}
						/>
					</div>
				</div>
			</div>

			<Modal
				open={open}
				title={strings?.payAccountTitle + name}
				onCancel={() => setOpen(false)}
				footer={(_, { OkBtn, CancelBtn }) => (
					<>
						<div className="flex items-end justify-end">
							<Button
								type="default"
								size="large"
								style={{
									backgroundColor: "#4f16c2",
									color: "#ffff",
								}}
								onClick={paymentForDriver}
							>
								{strings?.pay}
							</Button>
						</div>
					</>
				)}
			>
				<Divider />
				<div className="rounded-lg border-2 border-slate-100">
					<OrderDetailRow
						label={strings?.actualBalanc}
						value={`${currentBalance} ${CONFIG?.CURRENCY}`}
						style={style.oddRow}
						className="driverDetailRow"
					/>
					<OrderDetailRow
						label={strings?.incomeTotal}
						value={`${totalIncome} ${CONFIG?.CURRENCY}`}
						className="driverDetailRow"
					/>
					<OrderDetailRow
						label={strings?.totalPayment}
						value={`${totalPayment} ${
							CONFIG?.CURRENCY ? CONFIG?.CURRENCY : ""
						}`}
						style={style.oddRow}
						className="driverDetailRow"
					/>
					<OrderDetailRow
						label={strings?.lastIncome}
						value={
							`${lastIncome} ${CONFIG?.CURRENCY}` +
							"\n" +
							lastIncomeDate +
							"\n" +
							lastIncomeComment
						}
					/>
				</div>
				<Divider />
				<div className="flex flex-col gap-4">
					<div className="flex flex-col gap-2">
						<p className="font-Montserrat-Medium text-14 mt-1">
							{strings?.value} - {CONFIG?.CURRENCY}
						</p>
						<Input
							value={amount}
							onChange={(e) => setAmount(e.target.value)}
						/>
					</div>
					<div className="flex flex-col gap-2">
						<p className="font-Montserrat-Medium text-14 mt-1">
							{strings?.description}
						</p>
						<TextArea
							rows={4}
							value={comment}
							onChange={(e: any) => setComment(e.target.value)}
						/>
					</div>
					<div className="flex flex-col gap-2">
						<p>{strings?.type}</p>
						<Radio.Group
							onChange={onChange}
							value={value}
							className="flex flex-col"
						>
							<Radio value={1}>{strings?.radio1}</Radio>
							<Radio value={2}>{strings?.radio2}</Radio>
							{/* <Radio value={3}>Ndalese ne llogari</Radio> */}
						</Radio.Group>
					</div>
				</div>
			</Modal>
			<Modal
				open={openSettlementModal}
				title={strings?.settleAccount}
				onCancel={() => setOpenSettlementModal(false)}
				footer={(_, { OkBtn, CancelBtn }) => (
					<>
						<div className="flex items-end justify-end">
							<Button
								type="default"
								size="large"
								style={{
									backgroundColor: "#4f16c2",
									color: "#ffff",
								}}
								onClick={balanceAccount}
							>
								{strings?.settleAccount}
							</Button>
						</div>
					</>
				)}
			>
				<Divider />
				<div className="flex flex-col rounded-lg border-2 border-slate-100">
					<OrderDetailRow
						label={strings?.dailySalesLabel}
						value={
							dualCurrencyEnabled
								? `${dailySales} ${currencySymbol(
										CURRENCY_LEK
								  )}  •  ${dailySalesEur} ${currencySymbol(
										CURRENCY_EUR
								  )}`
								: dailySales
						}
						style={style.oddRow}
						className="driverDetailRow"
					/>
					<OrderDetailRow
						label={strings?.deliveredOrdersCount}
						value={data?.length}
						className="driverDetailRow"
					/>
					<OrderDetailRow
						label={strings?.lastChange}
						style={style.oddRow}
						className="driverDetailRow"
					/>
					<OrderDetailRow
						label={strings?.unsettledSales}
						value={
							dualCurrencyEnabled
								? `${
										salesOwedToCompany ?? 0.0
								  } ${currencySymbol(CURRENCY_LEK)}  •  ${
										salesOwedToCompanyEur ?? 0.0
								  } ${currencySymbol(CURRENCY_EUR)}`
								: salesOwedToCompany ?? 0.0
						}
						className="driverDetailRow"
					/>
				</div>
				<Divider />
				<div className=" flex flex-col gap-4">
					<div className="flex flex-col gap-4 ">
						{cashRegisterOptions?.length > 1 && (
							<DefaultSelect
								className={classes.input}
								onChange={setSelectedCashRegisterId}
								value={selectedCashRegisterId}
								placeholder={"Zgjedh nje Cash Register"}
								label={"Cash Register"}
								flexDirection="col"
								alignCenter="start"
								options={cashRegisterOptions}
								allowClear
							/>
						)}
						<p>
							{strings?.settleAccountDaily}{" "}
							{dualCurrencyEnabled
								? currencySymbol(CURRENCY_LEK)
								: CONFIG?.CURRENCY}
						</p>
						<DefaultInput
							value={cashIn}
							type="number"
							onChange={(e) => setCashIn(e.target.value)}
						/>
						{/* The driver hands in each currency separately, so each is counted
						    separately and becomes its own cash-register transaction. */}
						{dualCurrencyEnabled && (
							<>
								<p>
									{strings?.settleAccountDaily}{" "}
									{currencySymbol(CURRENCY_EUR)}
								</p>
								<DefaultInput
									value={cashInEur}
									type="number"
									onChange={(e) =>
										setCashInEur(e.target.value)
									}
								/>
							</>
						)}
						{/* <Input
							value={cashIn}
							type="number"
							onChange={(e) => setCashIn(e.target.value)}
						/> */}
					</div>
					<Alert
						type="warning"
						showIcon
						message={strings?.warningTitle}
						description={strings?.warningDescription}
					/>
				</div>
			</Modal>
			<Modal
				open={openFeeModal}
				title={strings?.driverFeesTable}
				onCancel={() => setOpenFeeModal(false)}
				footer={(_, { OkBtn, CancelBtn }) => (
					<>
						<div className="flex items-end justify-end">
							<Button
								type="default"
								size="large"
								style={{
									backgroundColor: "#4f16c2",
									color: "#ffff",
								}}
								onClick={() => setOpenSettlementModal(false)}
							>
								{strings?.saveChanges}
							</Button>
						</div>
					</>
				)}
			>
				<Divider />
				<div className="mb-10">
					<OrderDetailRow
						label={strings?.albania}
						style={style.oddRow}
						className="driverDetailRow"
					/>
					<OrderDetailRow
						label={strings?.kosovo}
						className="driverDetailRow"
					/>
					<OrderDetailRow
						label={strings?.macedonia}
						style={style.oddRow}
						className="driverDetailRow"
					/>
				</div>
			</Modal>
			<Modal
				open={openBlockingModal}
				onCancel={() => setOpenBlockingModal(false)}
				onOk={confirmBlocking}
				title={strings?.confirmBlockUser}
				okButtonProps={{ className: "bg-blue-500" }}
			>
				<p>{strings?.blockUserConfirmation}</p>
			</Modal>
			<Modal
				open={openDriverSettlementCashRegisterModal}
				title={`Detajet e Barazimit me Nr.${driverCashRegisterTransactionId}`}
				width={1000}
				onCancel={() => setOpenDriverSettlementCashRegisterModal(false)}
				cancelButtonProps={{ style: { display: "none" } }}
				okButtonProps={{ style: { display: "none" } }}
				bodyStyle={{ maxHeight: "80vh", overflowY: "auto" }}
			>
				<>
					<div>
						<Divider />
						<div
							className="ag-theme-alpine"
							style={{
								height: 500,
								width: "100%",
							}}
						>
							<AgGridReact
								rowData={settlementDriverOrderData}
								columnDefs={settlementDriverOrderTableColumns}
								defaultColDef={defaultColDef}
								suppressCopyRowsToClipboard={true}
								defaultExcelExportParams={
									defaultExcelExportSettings
								}
								domLayout="autoHeight"
								ref={settlementDriverOrderGridRef}
								enableRangeSelection={true}
								cacheQuickFilter={true}
								rowHeight={60}
							/>
						</div>
					</div>
				</>
			</Modal>
		</>
	);
};

export default DriverDetails;

const style = {
	oddRow: { backgroundColor: "#F9F8FC" },
};
